import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import date from 'date-and-time';
import { faListMusic } from '@fortawesome/pro-light-svg-icons/faListMusic';
import { TypedMemo } from '../../../common/types';
import { useTrackTopPlaylistAppearances } from '../../hooks/useTrackTopPlaylistAppearances';
import { useSMLDatePickerDateParam } from '../../hooks/useSMLDatePickerDateParam';
import { useSMLPaginationParam } from '../../hooks/useSMLPaginationParam';
import { useEntityIdParam } from '../../hooks/useEntityIdParam';
import { SMLResultsCard } from '../SMLResultsCard/SMLResultsCard';
import { SMLResultsItem } from '../SMLResultsCard/props';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { datePickerParamName } from '../SMLDatePicker/consts';
import { IntegrationTypeIcon } from '../IntegrationTypeIcon';

const paginationParamName = 'topPlaylistsPage';

const formatFollowers = (count: number, intl: ReturnType<typeof useIntl>): string => {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1)}M`;
  }
  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(1)}K`;
  }
  return intl.formatNumber(count);
};

const TrackTopPlaylistsCardComponent: React.FC = () => {
  const intl = useIntl();
  const trackId = useEntityIdParam('track');
  const { selectedDate, selectedDateValue } = useSMLDatePickerDateParam();
  const { currentPage } = useSMLPaginationParam(paginationParamName);

  const {
    data: playlistsData,
    isLoading,
    error,
    refetch,
  } = useTrackTopPlaylistAppearances({
    trackId: trackId ?? '',
    date: selectedDate,
    page: currentPage,
  });

  const playlists = useMemo(() => playlistsData?.playlists ?? [], [playlistsData?.playlists]);

  const items: SMLResultsItem[] = useMemo(
    () =>
      playlists.map((playlist) => ({
        id: playlist.playlistId,
        name: playlist.name,
        position: playlist.position + 1,
        imageUrl: playlist.imageUrl,
        trackImageUrls: playlist.trackImageUrls,
        rightElement: (
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <IntegrationTypeIcon integrationType={playlist.integrationType} size={20} />
            <span className="text-muted">{formatFollowers(playlist.followersCount, intl)}</span>
          </div>
        ),
        chart: { entityType: 'playlist' as const, date: selectedDate },
        route: SMLRoutes.playlistDetails,
        params: { playlistId: playlist.playlistId, [datePickerParamName]: selectedDateValue },
      })),
    [playlists, selectedDate, selectedDateValue, intl]
  );

  const title = intl.formatMessage({
    id: 'app.sml.track.topPlaylists.title',
    defaultMessage: 'Top playlists',
  });
  const subtitle = date.format(selectedDate, 'MMM DD, YYYY');

  return (
    <SMLResultsCard
      title={title}
      subtitle={subtitle}
      icon={faListMusic}
      iconColor="var(--brand-violet-lift)"
      items={items}
      isLoading={isLoading}
      error={error}
      onRetry={() => {
        refetch().catch(console.error);
      }}
      imageShape="rounded-square"
      paginationParamName={paginationParamName}
    />
  );
};

export const TrackTopPlaylistsCard = TypedMemo(TrackTopPlaylistsCardComponent);
